import React from "react";
import classnames from "classnames";
import { Link } from "react-router";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Label,
  FormGroup,
  InputGroup,
  Input,
  Row,
} from "reactstrap";
import Select from "react-select";
import ImageUploadContainer, { type PickedImage } from "../../User/adding-user/ImageUploadContainer";
import type { SelectOption } from "@/utils/selectOptions";

/** The Add New Site form (AddNewSiteContainer.tsx's state), also the POST /api/sites payload. */
export type AddNewSiteForm = {
  name: string;
  domain: string;
  canonial_url: string;
  country_code: string;
  language_code: string;
  design: { _id: string; name: string; source: string };
  logo_pic: string;
  active: boolean;
  analytics_code: string;
  gtm: string;
  blogdir?: string;
  special_style_files: string;
  special_script_files: string;
};

/** One design in the Design picker, as the container maps it from the store. */
export type DesignOption = { value: { _id: string }; label: string };

export type AddNewSiteViewProps = {
  site: AddNewSiteForm;
  loading: boolean;
  countryOptions: readonly SelectOption[];
  languageOptions: readonly SelectOption[];
  designOptions: DesignOption[];
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onChangeSelect: (option: SelectOption | null, name: string) => void;
  onChangeDesign: (option: DesignOption | null) => void;
  onClickAdd: (e: React.MouseEvent) => void;
  setLogoPic?: (img: PickedImage | null) => void;
  /** SCRUM-1817: the soft-deleted site a 409 named, and the payload that got the 409. */
  deletedHolder: { id: string; msg: string; value: string; payload: AddNewSiteForm } | null;
  onRestore: () => void;
  onStartFresh: () => void;
};

const AddNewSiteView = ({
  site,
  loading,
  countryOptions,
  languageOptions,
  designOptions,
  onChange,
  onChangeSelect,
  onChangeDesign,
  onClickAdd,
  setLogoPic,
  deletedHolder,
  onRestore,
  onStartFresh,
}: AddNewSiteViewProps) => {
  const handleImageUpload = (img: PickedImage) => setLogoPic && setLogoPic(img);

  return (
    <Container className="mt--6" fluid>
      <Row>
        <Col className="order-xl-1" xl="8">
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">Add New Site</h3>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              {/* Name */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Name</Label>
                <Col md="7">
                  <InputGroup className={classnames("input-group-merge")}>
                    <Input
                      placeholder="Name"
                      name="name"
                      type="text"
                      value={site.name}
                      onChange={onChange}
                    />
                  </InputGroup>
                </Col>
              </FormGroup>

              {/* Domain */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Domain</Label>
                <Col md="9">
                  <InputGroup className={classnames("input-group-merge")}>
                    <Input
                      placeholder="Domain"
                      name="domain"
                      type="textarea"
                      value={site.domain}
                      onChange={onChange}
                    />
                  </InputGroup>
                </Col>
              </FormGroup>

              {/* Canonical Url */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Canonical Url</Label>
                <Col md="9">
                  <InputGroup className={classnames("input-group-merge")}>
                    <Input
                      placeholder="Canonical Url"
                      name="canonial_url"
                      type="text"
                      value={site.canonial_url}
                      onChange={onChange}
                    />
                  </InputGroup>
                </Col>
              </FormGroup>

              {/* Country */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Country Code</Label>
                <Col md="7">
                  <Select
                    value={countryOptions.find(o => o.value === site.country_code) || null}
                    options={countryOptions}
                    classNamePrefix="select"
                    onChange={(opt) => onChangeSelect(opt, "country_code")}
                  />
                </Col>
              </FormGroup>

              {/* Language */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Language</Label>
                <Col md="7">
                  <Select
                    value={languageOptions.find(o => o.value === site.language_code) || null}
                    options={languageOptions}
                    classNamePrefix="select"
                    onChange={(opt) => onChangeSelect(opt, "language_code")}
                  />
                </Col>
              </FormGroup>

              {/* Design */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Design</Label>
                <Col md="7">
                  <Select
                    options={designOptions}
                    classNamePrefix="select"
                    onChange={onChangeDesign}
                  />
                </Col>
              </FormGroup>

              {/* Active */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Active</Label>
                <Col md="7">
                  <InputGroup className={classnames("input-group-merge")}>
                    <label className="custom-toggle custom-toggle-danger mr-1">
                      <input
                        type="checkbox"
                        name="active"
                        checked={!!site.active}
                        onChange={onChange}
                      />
                      <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="No"
                        data-label-on="Yes"
                      />
                    </label>
                  </InputGroup>
                </Col>
              </FormGroup>

              {/* Analytics code */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Analytics Code</Label>
                <Col md="9">
                  <InputGroup className={classnames("input-group-merge")}>
                    <Input
                      placeholder="Analytics Code"
                      name="analytics_code"
                      type="textarea"
                      value={site.analytics_code}
                      onChange={onChange}
                    />
                  </InputGroup>
                </Col>
              </FormGroup>

              {/* GTM container id — rendered as the GTM bootstrap on every
                  public page (SCRUM-744); GA4/UET live inside the container. */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">GTM Container ID</Label>
                <Col md="9">
                  <InputGroup className={classnames("input-group-merge")}>
                    <Input
                      placeholder="GTM-XXXXXXX"
                      name="gtm"
                      type="text"
                      value={site.gtm || ""}
                      onChange={onChange}
                    />
                  </InputGroup>
                </Col>
              </FormGroup>

              {/* Blog root — the Site's blogdir field, the root segment of the
                  blog breadcrumb (SCRUM-1235). Empty means the render-layer
                  default "blog"; the value is a segment, not a path. */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Blog root</Label>
                <Col md="9">
                  <InputGroup className={classnames("input-group-merge")}>
                    <Input
                      placeholder="blog"
                      name="blogdir"
                      type="text"
                      title="Root segment of the blog breadcrumb; leave empty for the default blog"
                      value={site.blogdir || ""}
                      onChange={onChange}
                    />
                  </InputGroup>
                </Col>
              </FormGroup>

              {/* Special style files */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Special Style Files</Label>
                <Col md="9">
                  <InputGroup className={classnames("input-group-merge")}>
                    <Input
                      placeholder="Special Style Files Code"
                      name="special_style_files"
                      type="textarea"
                      value={site.special_style_files}
                      onChange={onChange}
                    />
                  </InputGroup>
                </Col>
              </FormGroup>

              {/* Special script files */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">Special Script Files</Label>
                <Col md="9">
                  <InputGroup className={classnames("input-group-merge")}>
                    <Input
                      placeholder="Special Script Files Code"
                      name="special_script_files"
                      type="textarea"
                      value={site.special_script_files}
                      onChange={onChange}
                    />
                  </InputGroup>
                </Col>
              </FormGroup>

              {/*
                SCRUM-1817: a 409 from POST /api/sites names the soft-deleted
                site that still holds this domain or canonical URL. Two ways
                out, each saying what it does to the old site's pages.
              */}
              {deletedHolder && (
                <div className="alert alert-warning" role="alert">
                  <p className="mb-2">
                    {deletedHolder.msg}
                    {deletedHolder.value ? (
                      <>
                        {" "}
                        (<strong>{deletedHolder.value}</strong>)
                      </>
                    ) : null}
                    .
                  </p>
                  <p className="mb-2">
                    <Button
                      type="button"
                      size="sm"
                      color="warning"
                      className="mr-2"
                      onClick={onRestore}
                    >
                      Restore
                    </Button>
                    Restore brings the old site back, with its pages. This new
                    site is not created.
                  </p>
                  <p className="mb-0">
                    <Button
                      type="button"
                      size="sm"
                      outline
                      color="warning"
                      className="mr-2"
                      onClick={onStartFresh}
                    >
                      Start fresh
                    </Button>
                    Start fresh creates a new, empty site. The old pages stay
                    attached to the deleted site and do not move to the new one.
                  </p>
                </div>
              )}

              <Button color="primary" onClick={onClickAdd}>
                {loading ? "Adding..." : "Add"}
              </Button>
              <Button to="/admin/sites" tag={Link} className="btn btn-default ml-2">
                All Sites
              </Button>
            </CardBody>
          </Card>
        </Col>

        <Col className="order-xl-2" xl="4">
          <Card className="card-pricing border-0 text-center mb-4">
            <CardHeader className="bg-gradient-success">
              <h4 className="text-uppercase ls-0 text-white py-2 mb-0">
                Upload Logo
              </h4>
            </CardHeader>
            <CardBody className="px-lg-2">
              <ImageUploadContainer ImageUpload={handleImageUpload} />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AddNewSiteView;
